import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Container, Row, Card, CardHeader, Table, Input, Button, Badge } from "reactstrap"
import Header from "../../components/Headers/Header"
import { getCourierOrders, captureCourierOrder } from "../../apollo/server"
import { useQuery, useMutation, gql } from "@apollo/client"

interface CourierOrder {
  _id: string
  orderId: string
  orderStatus: string
  paymentStatus: string
  createdAt: string
  courierDescription: string
  courierBudget: number
  courierFee: number
  authorizedAmount: number
  capturedAmount: number
  courierPhoto: string
  user?: { _id: string; name: string; phone: string }
}

const yen = (n: number) => `¥${Math.round(n || 0).toLocaleString()}`

// 何でも配送(お使い便)の実費確定(capture)画面。配達後に実費を入力して本請求を確定する。
// 確定額 = min(実費 + 配達料, 与信額)。残りは自動で解放される。
const Courier: React.FC = () => {
  const { t } = useTranslation()
  const { loading, error, data, refetch } = useQuery(gql`${getCourierOrders}`, { fetchPolicy: "cache-and-network" })
  const [amounts, setAmounts] = useState<{ [id: string]: string }>({})
  const [rowError, setRowError] = useState<{ [id: string]: string }>({})

  const [capture, { loading: capturing }] = useMutation(gql`${captureCourierOrder}`, {
    onCompleted: () => refetch(),
    onError: e => console.log(e),
  })

  const orders: CourierOrder[] = (data && data.courierOrders) || []

  const onCapture = (o: CourierOrder) => {
    const raw = amounts[o._id]
    const actual = Number(raw)
    if (raw === undefined || raw === "" || !Number.isFinite(actual) || actual < 0) {
      setRowError(p => ({ ...p, [o._id]: t("CourierCaptureInvalid") }))
      return
    }
    const expected = Math.min(actual + (o.courierFee || 0), o.authorizedAmount || 0)
    if (!window.confirm(`${t("CourierCaptureConfirm")}\n${t("CourierCaptureCharge")}: ${yen(expected)}`)) return
    setRowError(p => ({ ...p, [o._id]: "" }))
    capture({ variables: { orderId: o._id, actualGoodsAmount: actual } })
  }

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Row className="mt-3">
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0">
                <h3 className="mb-0">{t("CourierCaptureTitle")}</h3>
                <small className="text-muted">{t("CourierCaptureHint")}</small>
              </CardHeader>

              {error && <p className="px-4">{`${t("Error")}! ${error.message}`}</p>}
              {loading && !data && <p className="px-4">{t("Loading")}</p>}
              {!loading && orders.length === 0 && <p className="px-4 text-muted">{t("CourierNoOrders")}</p>}

              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">{t("CourierOrderId")}</th>
                    <th scope="col">{t("CourierUser")}</th>
                    <th scope="col">{t("CourierDescription")}</th>
                    <th scope="col">{t("CourierBudget")}</th>
                    <th scope="col">{t("CourierFee")}</th>
                    <th scope="col">{t("CourierAuthorized")}</th>
                    <th scope="col">{t("CourierCaptured")}</th>
                    <th scope="col">{t("CourierActualAmount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map(o => {
                    const settled = o.paymentStatus === "PAID" || (o.capturedAmount || 0) > 0
                    return (
                      <tr key={o._id}>
                        <td>{o.orderId}</td>
                        <td>{o.user ? o.user.name : "-"}</td>
                        <td style={{ maxWidth: 220, whiteSpace: "normal" }}>{o.courierDescription}</td>
                        <td>{yen(o.courierBudget)}</td>
                        <td>{yen(o.courierFee)}</td>
                        <td>{yen(o.authorizedAmount)}</td>
                        <td>
                          {settled ? (
                            <Badge color="success" pill>
                              {t("CourierSettled")} {yen(o.capturedAmount)}
                            </Badge>
                          ) : (
                            <Badge color="warning" pill>
                              {t("CourierPending")}
                            </Badge>
                          )}
                        </td>
                        <td>
                          {settled ? (
                            "-"
                          ) : (
                            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 220 }}>
                              <Input
                                type="number"
                                min={0}
                                placeholder={t("CourierActualPlaceholder")}
                                value={amounts[o._id] ?? ""}
                                onChange={e => setAmounts(p => ({ ...p, [o._id]: e.target.value }))}
                                style={{ width: 120 }}
                              />
                              <Button color="primary" size="sm" disabled={capturing} onClick={() => onCapture(o)}>
                                {t("CourierCaptureButton")}
                              </Button>
                            </div>
                          )}
                          {rowError[o._id] ? <small className="text-danger d-block">{rowError[o._id]}</small> : null}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </Table>
            </Card>
          </div>
        </Row>
      </Container>
    </>
  )
}

export default Courier
